import { useEffect, useLayoutEffect, useState } from 'react';
import { SearchAiMode } from '../components/search/SearchAiToggle';

const STORAGE_KEY = 'dashboard-search-mode';

// Runs before paint on the client (unlike useEffect) so restoring the stored
// mode doesn't flash the default tab first;
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const readStoredMode = (): SearchAiMode | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'search' || stored === 'ai-chat' ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredMode = (mode: SearchAiMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // no op
  }
};

/**
 * Owns the Search/AI Chat toggle state for DashboardSearch: which mode is
 * active, and whether the just-activated input should receive focus. The
 * input text itself is owned by the caller (shared between Search and AI
 * Chat so it survives switching tabs).
 */
const useSearchAiToggle = (
  isNewDashboard: boolean,
  isOsmosisAiEligible: boolean,
) => {
  const [mode, setMode] = useState<SearchAiMode>('');
  // Only auto-focus the input when the tab was explicitly activated (click/Enter/Space),
  // not when arrow-key roving within the tablist.
  const [focusInputOnSwitch, setFocusInputOnSwitch] = useState(false);

  const handleModeChange = (
    newMode: SearchAiMode,
    options?: { focusInput?: boolean },
  ) => {
    setFocusInputOnSwitch(!!options?.focusInput);
    setMode(newMode);
    writeStoredMode(newMode);
  };

  useEffect(() => {
    if (focusInputOnSwitch) setFocusInputOnSwitch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Not eligible for OAI: always fall back to the old search design, even if isNewDashboard is on.
  const showNewSearchDesign = isNewDashboard && isOsmosisAiEligible;

  // Restore the user's last-used tab on mount (client only, avoids a
  // hydration mismatch since the initial `mode` state must match the server).
  useIsomorphicLayoutEffect(() => {
    if (showNewSearchDesign) {
      setMode(readStoredMode() ?? 'search');
    } else {
      setMode('search');
    }
  }, [showNewSearchDesign]);

  const isAiChatMode = showNewSearchDesign && mode === 'ai-chat';

  return {
    mode,
    focusInputOnSwitch,
    handleModeChange,
    showNewSearchDesign,
    isAiChatMode,
  };
};

export default useSearchAiToggle;
